import { createContext, useContext, useState, useCallback, type ReactNode, useEffect, useRef } from 'react'
import type { Patient } from '@/types'
import { patientApi } from '@services/patient.service'
import { recordRecentPatient } from '@lib/recentPatients'
import { clinicalStorageKey } from '@lib/clinicalSession'

interface ActivePatientContextType {
  activePatientId: string | null
  activePatientData: Patient | null
  isLoading: boolean
  setActivePatient: (patient: Patient) => void
  clearActivePatient: () => void
  refreshActivePatient: () => Promise<void>
}

const ActivePatientContext = createContext<ActivePatientContextType | undefined>(undefined)

export function ActivePatientProvider({ children }: { children: ReactNode }) {
  const [storageKey] = useState(() => clinicalStorageKey('activePatientId'))
  const [activePatientId, setActivePatientId] = useState<string | null>(null)
  const [activePatientData, setActivePatientData] = useState<Patient | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const revision = useRef(0)

  // NOM-004 NO-04: never persist clinical data (PHI) in browser storage. Only
  // the opaque patient ID is kept for navigation continuity across refreshes;
  // the clinical payload is always re-fetched from the API.
  const setActivePatient = useCallback((patient: Patient) => {
    revision.current += 1
    setIsLoading(false)
    setActivePatientId(patient.id)
    setActivePatientData(patient)
    sessionStorage.setItem(storageKey, patient.id)
    // Record only the opaque ID — no PHI in localStorage (NOM-004 NO-04).
    recordRecentPatient(patient.id)
  }, [storageKey])

  const clearActivePatient = useCallback(() => {
    revision.current += 1
    setIsLoading(false)
    setActivePatientId(null)
    setActivePatientData(null)
    sessionStorage.removeItem(storageKey)
  }, [storageKey])

  const refreshActivePatient = useCallback(async () => {
    if (!activePatientId) return

    const requestRevision = ++revision.current
    setIsLoading(true)
    try {
      const patient = await patientApi.getPatient(activePatientId)
      if (revision.current === requestRevision && patient.id === activePatientId) {
        setActivePatientData(patient)
      }
    } catch {
      // No cached PHI fallback by design (NO-04); leave data as-is.
    } finally {
      if (revision.current === requestRevision) setIsLoading(false)
    }
  }, [activePatientId])

  // Restore from sessionStorage on mount — re-fetch PHI from the API, never
  // read clinical data back from browser storage.
  useEffect(() => {
    const requestRevision = ++revision.current
    const cachedId = sessionStorage.getItem(storageKey)
    if (!cachedId) return () => { revision.current += 1 }
    setActivePatientId(cachedId)
    setIsLoading(true)
    patientApi
      .getPatient(cachedId)
      .then((patient) => {
        if (revision.current !== requestRevision) return
        if (patient.id !== cachedId) {
          clearActivePatient()
          return
        }
        setActivePatientData(patient)
      })
      .catch(() => {
        if (revision.current === requestRevision) clearActivePatient()
      })
      .finally(() => {
        if (revision.current === requestRevision) setIsLoading(false)
      })
    return () => { revision.current += 1 }
  }, [clearActivePatient, storageKey])

  return (
    <ActivePatientContext.Provider
      value={{
        activePatientId,
        activePatientData,
        isLoading,
        setActivePatient,
        clearActivePatient,
        refreshActivePatient,
      }}
    >
      {children}
    </ActivePatientContext.Provider>
  )
}

export function useActivePatient() {
  const context = useContext(ActivePatientContext)
  if (context === undefined) {
    throw new Error('useActivePatient must be used within an ActivePatientProvider')
  }
  return context
}

export function useIsInPatientWorkspace() {
  const context = useContext(ActivePatientContext)
  if (context === undefined) return false
  return context.activePatientId !== null
}
